import { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import {
  Menu,
  X,
  Users,
  Building,
  CreditCard,
  Calculator,
  Bell,
  Code,
  LayoutDashboard,
  Layers,
  Megaphone,
  CalendarDays,
  Send,
  Ticket,
  Wallet,
  ChevronDown,
  ScrollText,
  Activity,
  Dog,
  Settings,
  GalleryHorizontalEnd,
  Smartphone,
  TrendingUp,
  type LucideIcon,
} from 'lucide-react';
import { useUIStore } from '@/store/uiStore';

/* ─────────────────────────────────────────────────────────────────
 *  메뉴 정의 — 여기 배열만 수정하면 사이드바 구성이 바뀐다.
 *  item  : 단독 링크
 *  group : 접이식 그룹 (children 경로 기준으로 활성/자동 열림 판단)
 * ───────────────────────────────────────────────────────────────── */

interface NavLeaf {
  path: string;
  text: string;
  badge?: number;
}

type NavEntry =
  | { type: 'item'; icon: string; path: string; text: string; badge?: number }
  | { type: 'group'; key: string; icon: string; text: string; children: NavLeaf[] };

const NAV: NavEntry[] = [
  { type: 'item', icon: 'dashboard', path: '/dashboard', text: '대시보드' },

  {
    type: 'group', key: 'users', icon: 'users', text: '회원·매장 관리',
    children: [
      { path: '/members', text: '회원 목록' },
      { path: '/business', text: '매장 목록' },
      { path: '/business-menu', text: '사업자 메뉴 관리' },
    ],
  },

  {
    type: 'group', key: 'reservation', icon: 'calendar-days', text: '예약·이용권',
    children: [
      { path: '/daily-reservations', text: '일자별 예약' },
      { path: '/tickets', text: '이용권 관리' },
    ],
  },

  {
    type: 'group', key: 'payment', icon: 'credit-card', text: '결제 및 매출/정산',
    children: [
      { path: '/payment/credit', text: '크레딧 결제' },
      { path: '/payment/online', text: '온라인 결제' },
      { path: '/payment/offline', text: '오프라인 결제' },
      { path: '/settlement', text: '매출/정산' },
    ],
  },

  /* 크레딧 관리
   *  ※ 지급 정책(policies) 은 1차 출시에 미도입 — 추후 활성화 시
   *     { path: '/credit/policies', text: '지급 정책' } 한 줄만 다시 추가하면 됨.
   *     라우트·페이지·BE API 는 그대로 유지. */
  {
    type: 'group', key: 'credit', icon: 'wallet', text: '크레딧 관리',
    children: [
      { path: '/credit/dashboard', text: '크레딧 현황' },
      { path: '/credit/prices', text: '서비스 금액' },
      { path: '/credit/packages', text: '충전 패키지' },
      { path: '/credit/changes', text: '변경 이력' },
    ],
  },

  {
    type: 'group', key: 'content', icon: 'megaphone', text: '알림·콘텐츠',
    children: [
      { path: '/announcements', text: '공지사항' },
      { path: '/banners', text: '홈 배너' },
      { path: '/inquiries', text: '문의사항' },
      { path: '/push-messages', text: 'Push 발송' },
      { path: '/kakao-alim/templates', text: '알림톡 템플릿' },
      { path: '/kakao-alim/logs', text: '알림톡 발송 이력' },
      // 알림장 메뉴 비노출 (요구사항 재정의 전까지 보류)
      // { path: '/notifications', text: '알림장', badge: 3 },
    ],
  },

  {
    type: 'group', key: 'logs', icon: 'scroll-text', text: '로그·감사',
    children: [
      { path: '/business-logs', text: '사업자 활동 로그' },
      { path: '/guardian-logs', text: '보호자 활동 로그' },
      { path: '/audit-logs', text: 'API 감사 로그' },
    ],
  },

  {
    type: 'group', key: 'settings', icon: 'settings', text: '운영 설정',
    children: [
      { path: '/conditions', text: '조건 설정' },
      { path: '/terms', text: '약관 관리' },
      { path: '/app-version-policies', text: '앱 버전 정책' },
      // API 테스트 메뉴 비노출 (개발용 도구)
      // { path: '/api-test', text: 'API 테스트' },
    ],
  },
];

const ICONS: Record<string, LucideIcon> = {
  dashboard: LayoutDashboard,
  users: Users,
  building: Building,
  'credit-card': CreditCard,
  calculator: Calculator,
  bell: Bell,
  megaphone: Megaphone,
  banner: GalleryHorizontalEnd,
  send: Send,
  'calendar-days': CalendarDays,
  ticket: Ticket,
  layers: Layers,
  code: Code,
  'scroll-text': ScrollText,
  activity: Activity,
  dog: Dog,
  smartphone: Smartphone,
  settings: Settings,
  wallet: Wallet,
  revenue: TrendingUp,
};

/** 경로 활성 판단 — 하위 상세 경로(/members/3 등)도 부모 메뉴를 활성화 */
function isPathActive(pathname: string, path: string): boolean {
  if (path === '/dashboard') return pathname === '/dashboard';
  return pathname === path || pathname.startsWith(`${path}/`);
}

function isGroupActive(pathname: string, group: Extract<NavEntry, { type: 'group' }>): boolean {
  return group.children.some(c => isPathActive(pathname, c.path));
}

/**
 * 사이드바
 * - 데스크톱: 접기/펴기(w-16/w-64), 항상 보임
 * - 모바일: 드로어(w-64), 토글 시 슬라이드
 */
export function Sidebar() {
  const { pathname } = useLocation();
  const { isSidebarOpen, isMobile, setSidebarState } = useUIStore();

  // 그룹 열림 상태 — 초기값: 현재 경로가 속한 그룹만 열림
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {};
    for (const entry of NAV) {
      if (entry.type === 'group') init[entry.key] = isGroupActive(pathname, entry);
    }
    return init;
  });

  // 경로 이동 시 해당 경로가 속한 그룹은 자동으로 펼침 (다른 그룹은 건드리지 않음)
  useEffect(() => {
    setOpenGroups(prev => {
      let changed = false;
      const next = { ...prev };
      for (const entry of NAV) {
        if (entry.type === 'group' && isGroupActive(pathname, entry) && !next[entry.key]) {
          next[entry.key] = true;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [pathname]);

  const toggleGroup = (key: string) =>
    setOpenGroups(prev => ({ ...prev, [key]: !prev[key] }));

  const closeOnMobile = () => {
    if (isMobile) setSidebarState('collapsed');
  };

  const renderIcon = (name: string, active: boolean) => {
    const Icon = ICONS[name] ?? Menu;
    return (
      <Icon
        size={20}
        className={`flex-shrink-0 ${active ? 'text-primary-600' : 'text-gray-500'}`}
      />
    );
  };

  // 폭 클래스: 모바일은 항상 w-64(드로어), 데스크톱은 토글
  const widthClass = isMobile ? 'w-64' : isSidebarOpen ? 'w-64' : 'w-16';

  // 가시성: 모바일 + 닫힘 → 화면 밖
  const translateClass =
    isMobile && !isSidebarOpen ? '-translate-x-full' : 'translate-x-0';

  // 라벨 표시 여부: 모바일이면 항상 보임(드로어 폭 충분), 데스크톱은 열렸을 때만
  const showLabel = isMobile || isSidebarOpen;

  const topLevelClass = (active: boolean) =>
    `flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
      active
        ? 'bg-primary-50 text-primary-600'
        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
    } ${!showLabel ? 'justify-center' : ''}`;

  return (
    <aside
      className={`fixed left-0 top-16 z-50 h-[calc(100vh-4rem)] border-r border-gray-200 bg-white transform transition-[width,transform] duration-300 ease-in-out ${widthClass} ${translateClass}`}
    >
      {isMobile && (
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
          <h2 className="text-base font-semibold text-gray-900">메뉴</h2>
          <button
            onClick={closeOnMobile}
            className="rounded-md p-1.5 hover:bg-gray-100"
            aria-label="사이드바 닫기"
          >
            <X size={18} />
          </button>
        </div>
      )}

      <nav className="h-full overflow-y-auto overflow-x-hidden pb-4">
        <ul className="space-y-1 p-2">
          {NAV.map(entry => {
            if (entry.type === 'item') {
              const active = isPathActive(pathname, entry.path);
              return (
                <li key={entry.path}>
                  <Link
                    to={entry.path}
                    onClick={closeOnMobile}
                    className={topLevelClass(active)}
                    title={!showLabel ? entry.text : undefined}
                  >
                    {renderIcon(entry.icon, active)}
                    {showLabel && <span className="flex-1 truncate">{entry.text}</span>}
                    {showLabel && entry.badge ? (
                      <span className="min-w-[20px] rounded-full bg-red-500 px-2 py-0.5 text-center text-xs text-white">
                        {entry.badge}
                      </span>
                    ) : null}
                  </Link>
                </li>
              );
            }

            const groupActive = isGroupActive(pathname, entry);
            const open = !!openGroups[entry.key];
            return (
              <li key={entry.key}>
                <button
                  type="button"
                  onClick={() => toggleGroup(entry.key)}
                  className={topLevelClass(groupActive)}
                  title={!showLabel ? entry.text : undefined}
                >
                  {renderIcon(entry.icon, groupActive)}
                  {showLabel && (
                    <>
                      <span className="flex-1 truncate text-left">{entry.text}</span>
                      <ChevronDown
                        size={16}
                        className={`transition-transform ${open ? 'rotate-180' : ''}`}
                      />
                    </>
                  )}
                </button>

                {showLabel && open && (
                  <ul className="mt-1 space-y-0.5 pl-9">
                    {entry.children.map(c => {
                      const childActive = isPathActive(pathname, c.path);
                      return (
                        <li key={c.path}>
                          <Link
                            to={c.path}
                            onClick={closeOnMobile}
                            className={`flex items-center gap-2 rounded-md px-3 py-1.5 text-sm transition-colors ${
                              childActive
                                ? 'bg-primary-50 font-semibold text-primary-600'
                                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                            }`}
                          >
                            <span className="flex-1 truncate">{c.text}</span>
                            {c.badge ? (
                              <span className="min-w-[20px] rounded-full bg-red-500 px-2 py-0.5 text-center text-xs text-white">
                                {c.badge}
                              </span>
                            ) : null}
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      </nav>
    </aside>
  );
}
